import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Ticket, Plus, Trash2, Loader2, Copy, Power } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function CouponsAdminPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", type: "percent", value: 50, max_uses: 100,
    valid_until: "", applies_to_pro: true, applies_to_club: true, notes: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/coupons`);
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") {
    return (
      <Card className="p-10 text-center border-red-200 bg-red-50 max-w-md mx-auto">
        <p className="text-red-700">Sezione riservata agli amministratori.</p>
      </Card>
    );
  }

  const onSave = async () => {
    if (!form.code.trim()) return toast.error("Inserisci un codice");
    if (form.value <= 0) return toast.error("Valore sconto deve essere positivo");
    const applies_to = [];
    if (form.applies_to_pro) applies_to.push("pro");
    if (form.applies_to_club) applies_to.push("club");
    if (applies_to.length === 0) return toast.error("Seleziona almeno un piano");
    setSaving(true);
    try {
      await api.post(`/coupons`, {
        code: form.code.trim().toUpperCase(),
        type: form.type,
        value: Number(form.value),
        max_uses: Number(form.max_uses),
        applies_to,
        notes: form.notes.trim() || null,
        valid_until: form.valid_until || null,
        active: true,
      });
      toast.success(`Codice ${form.code.toUpperCase()} creato`);
      setOpen(false);
      setForm({ code: "", type: "percent", value: 50, max_uses: 100, valid_until: "", applies_to_pro: true, applies_to_club: true, notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    }
    setSaving(false);
  };

  const onDelete = async (code) => {
    if (!window.confirm(`Eliminare il codice ${code}? Sarà rimosso definitivamente.`)) return;
    try {
      await api.delete(`/coupons/${code}`);
      toast.success("Codice eliminato");
      load();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Codice ${code} copiato`);
  };

  const formatDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT") : "—");

  return (
    <div className="space-y-6 max-w-5xl" data-testid="coupons-admin-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Ticket size={12} /> Promozioni
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Codici sconto</h1>
          <p className="text-slate-500">Crea e gestisci i codici promozionali per acquisire nuove società.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-sky-500 hover:bg-sky-600" data-testid="new-coupon-btn">
          <Plus size={14} className="mr-1" /> Nuovo codice
        </Button>
      </div>

      {loading && (
        <Card className="p-8 text-center"><Loader2 className="mx-auto animate-spin text-slate-400" /></Card>
      )}

      {!loading && items.length === 0 && (
        <Card className="p-10 border-slate-200 text-center">
          <Ticket size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-700">Nessun codice creato</p>
          <p className="text-xs text-slate-500 mt-1">Crea un codice sconto per offrire promozioni alle società.</p>
        </Card>
      )}

      {!loading && items.length > 0 && (
        <Card className="p-0 overflow-hidden border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Codice</th>
                <th className="text-left px-4 py-3">Sconto</th>
                <th className="text-left px-4 py-3">Utilizzi</th>
                <th className="text-left px-4 py-3">Piani</th>
                <th className="text-left px-4 py-3">Scadenza</th>
                <th className="text-left px-4 py-3">Note</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const usedUp = c.uses >= c.max_uses;
                return (
                  <tr key={c.code} className="border-t border-slate-100" data-testid={`coupon-row-${c.code}`}>
                    <td className="px-4 py-3 font-mono font-bold">{c.code}</td>
                    <td className="px-4 py-3">
                      {c.type === "percent" ? `-${c.value}%` : `-€${c.value}`}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className={usedUp ? "text-red-600 font-semibold" : "text-slate-700"}>
                          {c.uses} / {c.max_uses}
                        </span>
                        <div className="w-16 h-1 rounded-full bg-slate-200 overflow-hidden">
                          <div
                            className={`h-full ${usedUp ? "bg-red-500" : "bg-emerald-500"}`}
                            style={{ width: `${Math.min(100, (c.uses / c.max_uses) * 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {(c.applies_to || []).map((p) => (
                          <span key={p} className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">{p}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(c.valid_until)}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate" title={c.notes}>{c.notes || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => copyCode(c.code)} title="Copia codice">
                        <Copy size={13} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => onDelete(c.code)} className="text-red-500">
                        <Trash2 size={13} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="new-coupon-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuovo codice sconto</DialogTitle>
            <DialogDescription>Crea un nuovo codice promozionale per acquisire o premiare società.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Codice *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="EARLYBIRD50" className="font-mono uppercase" data-testid="coupon-code-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Tipo *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentuale (%)</SelectItem>
                    <SelectItem value="fixed">Importo fisso (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valore *</Label>
                <Input type="number" min="1" step="1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Utilizzi massimi</Label>
                <Input type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} />
              </div>
              <div>
                <Label>Scadenza</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Validi su</Label>
              <div className="flex gap-2 mt-1">
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer">
                  <input type="checkbox" checked={form.applies_to_pro} onChange={(e) => setForm({ ...form, applies_to_pro: e.target.checked })} />
                  <span className="text-sm">Pro</span>
                </label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer">
                  <input type="checkbox" checked={form.applies_to_club} onChange={(e) => setForm({ ...form, applies_to_club: e.target.checked })} />
                  <span className="text-sm">Club</span>
                </label>
              </div>
            </div>
            <div>
              <Label>Note interne (opzionale)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="es. campagna Facebook ottobre" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={onSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="save-coupon-btn">
              {saving && <Loader2 size={13} className="mr-1 animate-spin" />}
              Crea codice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
